// MARK://dhk8/pos/lib/bridge/printing-config-store
// Configuración local (POR TERMINAL) del comportamiento del bridge de impresión.
// 100% en localStorage — no backend, no sync entre terminales. Cada NUC SAVIs
// guarda sus propias preferencias.
//
// Forma persistida (`dhk8_pos_printing_config_v1`):
//   {
//     defaultPrinter: string | null         — nombre Windows de la impresora elegida
//     autoPrint: {
//       barra:   { enabled, copies }        — POST /v1/print al cobrar en /pos/barra
//       comanda: { enabled, copies }        — POST /v1/print al cobrar en /order (mesa)
//       kds:     { enabled, copies }        — POST /v1/print al fire to kitchen
//     }
//     drawerOnPayment: {                    — abrir cajón post-pago por método
//       CASH, CARD_DEBIT, CARD_CREDIT, TRANSFER, MEAL_VOUCHER, OTHER
//     }
//     bridge: {                             — a qué Bridge habla esta terminal
//       mode: 'host' | 'relay'              — host = loopback (USB local); relay = vía nube (Print Relay)
//       stationId: string | null            — id de la estación nube resuelta (solo en mode 'relay')
//       stationName: string | null          — nombre legible de la estación (solo en mode 'relay')
//       host: string | null                 — LEGACY: solo se conserva para migración (modo 'client' viejo)
//       token: string | null                — LEGACY: solo se conserva para migración
//     }
//     history: [{ at, kind, result, error? }] — últimas N impresiones (sección 5)
//   }
//
// Migración (Print Relay, 2026-06-16): el modo 'client' (Bridge en otro equipo de
// la LAN, http directo) se reemplaza por 'relay' (encolar en la nube y que el
// Bridge HOST jale los jobs — adiós mixed-content). Al cargar, un blob viejo con
// `mode: 'client'` se mapea a `'relay'`; `host`/`token` se conservan SOLO por si
// hiciera falta inspección post-migración, pero el relay-client NO los usa.
//
// Defaults:
//   - autoPrint todos ON con copies=1
//   - drawerOnPayment solo CASH=true (el resto OFF)
//   - bridge mode 'host' (loopback 127.0.0.1 — comportamiento histórico, back-compat)
//   - history vacío
//
// Tolerante a corruption: try/catch en carga, reset al default si el blob no es
// parseable o no matchea el shape — sin perder la funcionalidad del POS.

import { create } from 'zustand'

const STORAGE_KEY = 'dhk8_pos_printing_config_v1'
const HISTORY_MAX = 5

export type SurfaceKey = 'barra' | 'comanda' | 'kds'

export type PaymentMethodKey =
  | 'CASH'
  | 'CARD_DEBIT'
  | 'CARD_CREDIT'
  | 'TRANSFER'
  | 'MEAL_VOUCHER'
  | 'OTHER'

export interface AutoPrintSetting {
  enabled: boolean
  /** 1..3 (kds 1..2). El UI clamp-ea, este store no enforz-a límite duro
   *  para que un valor viejo no rompa el load. */
  copies: number
}

export interface PrintHistoryEntry {
  /** ISO timestamp. */
  at: string
  /** Texto corto: "receipt", "kitchen", "drawer", "test-page", … */
  kind: string
  result: 'ok' | 'error'
  error?: string
}

export type BridgeMode = 'host' | 'relay'

/** Destino del Bridge para ESTA terminal.
 *  - 'host'  → loopback 127.0.0.1:9101 (el Bridge corre en este mismo equipo, USB local).
 *  - 'relay' → vía nube (Print Relay): las impresiones se encolan en `POST …/print/jobs`
 *    y el Bridge HOST de la estación (`stationId`) las jala e imprime local. El navegador
 *    NUNCA habla a la LAN → adiós mixed-content desde el POS https.
 *  `stationId`/`stationName` solo aplican en mode 'relay'. `host`/`token` son LEGACY
 *  (solo migración del viejo modo 'client'); ningún cliente los usa hoy. */
/** Estación que ESTE equipo (HOST) publicó en la nube — informativo para la UI de
 *  Ajustes. No cambia el modo de impresión (el HOST sigue por loopback); solo
 *  recuerda que ya registró su bridge para que los satélites le impriman. */
export interface PublishedStation {
  stationId: string
  name: string
  pairingCode: string
}

export interface BridgeConnection {
  mode: BridgeMode
  /** Id de la estación nube resuelta vía pairingCode (mode 'relay'). null = sin vincular. */
  stationId: string | null
  /** Nombre legible de la estación (mode 'relay') — para mostrar en Ajustes. null = sin vincular. */
  stationName: string | null
  /** LEGACY (migración 'client'): IP/hostname viejo del equipo HOST. No se usa. */
  host: string | null
  /** LEGACY (migración 'client'): token de emparejamiento viejo. No se usa. */
  token: string | null
  /** Estación publicada por este HOST en la nube (ver PublishedStation). null = no publicada desde aquí. */
  publishedStation: PublishedStation | null
}

export interface PrintingConfig {
  defaultPrinter: string | null
  autoPrint: Record<SurfaceKey, AutoPrintSetting>
  drawerOnPayment: Record<PaymentMethodKey, boolean>
  bridge: BridgeConnection
  history: PrintHistoryEntry[]
}

const DEFAULT_CONFIG: PrintingConfig = {
  defaultPrinter: null,
  autoPrint: {
    barra: { enabled: true, copies: 1 },
    comanda: { enabled: true, copies: 1 },
    kds: { enabled: true, copies: 1 },
  },
  drawerOnPayment: {
    CASH: true,
    CARD_DEBIT: false,
    CARD_CREDIT: false,
    TRANSFER: false,
    MEAL_VOUCHER: false,
    OTHER: false,
  },
  bridge: {
    mode: 'host',
    stationId: null,
    stationName: null,
    host: null,
    token: null,
    publishedStation: null,
  },
  history: [],
}

// ── persistencia manual (más control que `persist` para sanitizar shape) ────

function clamp(n: unknown, min: number, max: number, fallback: number): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return fallback
  return Math.max(min, Math.min(max, Math.round(n)))
}

function loadFromStorage(): PrintingConfig {
  if (typeof localStorage === 'undefined') return cloneDefault()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return cloneDefault()
    const parsed = JSON.parse(raw) as Partial<PrintingConfig> | null
    if (!parsed || typeof parsed !== 'object') return cloneDefault()
    return mergeWithDefaults(parsed)
  } catch {
    // Corrupted — reset to defaults but don't throw.
    return cloneDefault()
  }
}

function cloneDefault(): PrintingConfig {
  return {
    defaultPrinter: null,
    autoPrint: {
      barra: { ...DEFAULT_CONFIG.autoPrint.barra },
      comanda: { ...DEFAULT_CONFIG.autoPrint.comanda },
      kds: { ...DEFAULT_CONFIG.autoPrint.kds },
    },
    drawerOnPayment: { ...DEFAULT_CONFIG.drawerOnPayment },
    bridge: { ...DEFAULT_CONFIG.bridge },
    history: [],
  }
}

/** Saneo del bloque bridge + MIGRACIÓN de blobs viejos:
 *  - mode: 'relay' | 'host'. El valor legacy 'client' se migra a 'relay'
 *    (back-compat — terminales que apuntaban a un Bridge LAN pasan a vía nube).
 *    Cualquier otro valor → 'host'.
 *  - stationId/stationName: strings no vacíos o null (mode 'relay').
 *  - host/token: se conservan tal cual (legacy, solo inspección) o null.
 *  Una config vieja sin `bridge` cae a HOST (loopback, sin cambio). */
/** Saneo de la estación publicada por este HOST (PublishedStation). Cualquier blob
 *  que no traiga los 3 campos string válidos → null. */
function mergePublished(raw: unknown): PublishedStation | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Partial<PublishedStation>
  if (
    typeof r.stationId === 'string' && r.stationId.trim().length > 0 &&
    typeof r.name === 'string' &&
    typeof r.pairingCode === 'string'
  ) {
    return { stationId: r.stationId, name: r.name, pairingCode: r.pairingCode }
  }
  return null
}

function mergeBridge(raw: unknown): BridgeConnection {
  const def = DEFAULT_CONFIG.bridge
  if (!raw || typeof raw !== 'object') return { ...def }
  const r = raw as Omit<Partial<BridgeConnection>, 'mode'> & { mode?: string }
  // Migración: 'client' (viejo LAN-directo) → 'relay' (vía nube). `mode` se lee como
  // string crudo del blob persistido (puede ser el legacy 'client', no solo BridgeMode).
  const rawMode: string | undefined = r.mode
  const mode: BridgeMode = rawMode === 'relay' || rawMode === 'client' ? 'relay' : 'host'
  const stationId = typeof r.stationId === 'string' && r.stationId.trim().length > 0 ? r.stationId.trim() : null
  const stationName = typeof r.stationName === 'string' && r.stationName.trim().length > 0 ? r.stationName.trim() : null
  const host = typeof r.host === 'string' && r.host.trim().length > 0 ? r.host.trim() : null
  const token = typeof r.token === 'string' && r.token.trim().length > 0 ? r.token.trim() : null
  const publishedStation = mergePublished(r.publishedStation)
  return { mode, stationId, stationName, host, token, publishedStation }
}

function mergeAutoPrint(raw: unknown, key: SurfaceKey): AutoPrintSetting {
  const def = DEFAULT_CONFIG.autoPrint[key]
  if (!raw || typeof raw !== 'object') return { ...def }
  const r = raw as Partial<AutoPrintSetting>
  const max = key === 'kds' ? 2 : 3
  return {
    enabled: typeof r.enabled === 'boolean' ? r.enabled : def.enabled,
    copies: clamp(r.copies, 1, max, def.copies),
  }
}

function mergeWithDefaults(raw: Partial<PrintingConfig>): PrintingConfig {
  const rawDrawer = (raw.drawerOnPayment ?? {}) as Partial<Record<PaymentMethodKey, boolean>>
  const drawer: Record<PaymentMethodKey, boolean> = {
    CASH: typeof rawDrawer.CASH === 'boolean' ? rawDrawer.CASH : DEFAULT_CONFIG.drawerOnPayment.CASH,
    CARD_DEBIT: typeof rawDrawer.CARD_DEBIT === 'boolean' ? rawDrawer.CARD_DEBIT : DEFAULT_CONFIG.drawerOnPayment.CARD_DEBIT,
    CARD_CREDIT: typeof rawDrawer.CARD_CREDIT === 'boolean' ? rawDrawer.CARD_CREDIT : DEFAULT_CONFIG.drawerOnPayment.CARD_CREDIT,
    TRANSFER: typeof rawDrawer.TRANSFER === 'boolean' ? rawDrawer.TRANSFER : DEFAULT_CONFIG.drawerOnPayment.TRANSFER,
    MEAL_VOUCHER: typeof rawDrawer.MEAL_VOUCHER === 'boolean' ? rawDrawer.MEAL_VOUCHER : DEFAULT_CONFIG.drawerOnPayment.MEAL_VOUCHER,
    OTHER: typeof rawDrawer.OTHER === 'boolean' ? rawDrawer.OTHER : DEFAULT_CONFIG.drawerOnPayment.OTHER,
  }
  const rawAuto = (raw.autoPrint ?? {}) as Partial<Record<SurfaceKey, unknown>>
  const history = Array.isArray(raw.history)
    ? raw.history.filter((h): h is PrintHistoryEntry =>
        !!h && typeof h === 'object' && typeof (h as PrintHistoryEntry).at === 'string'
        && typeof (h as PrintHistoryEntry).kind === 'string'
        && ((h as PrintHistoryEntry).result === 'ok' || (h as PrintHistoryEntry).result === 'error'))
        .slice(0, HISTORY_MAX)
    : []
  return {
    defaultPrinter: typeof raw.defaultPrinter === 'string' && raw.defaultPrinter.length > 0 ? raw.defaultPrinter : null,
    autoPrint: {
      barra: mergeAutoPrint(rawAuto['barra'], 'barra'),
      comanda: mergeAutoPrint(rawAuto['comanda'], 'comanda'),
      kds: mergeAutoPrint(rawAuto['kds'], 'kds'),
    },
    drawerOnPayment: drawer,
    bridge: mergeBridge(raw.bridge),
    history,
  }
}

function saveToStorage(cfg: PrintingConfig): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg))
  } catch {
    // Quota / private mode — silencioso.
  }
}

// ── store ────────────────────────────────────────────────────────────────────

export interface PrintingConfigState extends PrintingConfig {
  setDefaultPrinter: (name: string | null) => void
  setAutoPrint: (key: SurfaceKey, patch: Partial<AutoPrintSetting>) => void
  setDrawerOnPayment: (method: PaymentMethodKey, value: boolean) => void
  setBridge: (patch: Partial<BridgeConnection>) => void
  pushHistory: (entry: Omit<PrintHistoryEntry, 'at'> & { at?: string }) => void
  clearHistory: () => void
  resetToDefaults: () => void
}

export const usePrintingConfigStore = create<PrintingConfigState>((set, get) => {
  const initial = loadFromStorage()
  const persist = () => {
    const { setDefaultPrinter: _a, setAutoPrint: _b, setDrawerOnPayment: _c,
            setBridge: _g, pushHistory: _d, clearHistory: _e, resetToDefaults: _f, ...rest } = get()
    void _a; void _b; void _c; void _d; void _e; void _f; void _g
    saveToStorage(rest)
  }
  return {
    ...initial,
    setDefaultPrinter: (name) => {
      set({ defaultPrinter: name && name.length > 0 ? name : null })
      persist()
    },
    setAutoPrint: (key, patch) => {
      const max = key === 'kds' ? 2 : 3
      set((s) => ({
        autoPrint: {
          ...s.autoPrint,
          [key]: {
            enabled: typeof patch.enabled === 'boolean' ? patch.enabled : s.autoPrint[key].enabled,
            copies: patch.copies !== undefined ? clamp(patch.copies, 1, max, s.autoPrint[key].copies) : s.autoPrint[key].copies,
          },
        },
      }))
      persist()
    },
    setDrawerOnPayment: (method, value) => {
      set((s) => ({ drawerOnPayment: { ...s.drawerOnPayment, [method]: value } }))
      persist()
    },
    setBridge: (patch) => {
      set((s) => {
        const next: BridgeConnection = { ...s.bridge }
        if (patch.mode !== undefined) {
          // Acepta 'client' como alias legacy de 'relay' por si algún caller viejo lo manda.
          next.mode = patch.mode === 'relay' || (patch.mode as string) === 'client' ? 'relay' : 'host'
        }
        if (patch.stationId !== undefined) {
          next.stationId = typeof patch.stationId === 'string' && patch.stationId.trim().length > 0 ? patch.stationId.trim() : null
        }
        if (patch.stationName !== undefined) {
          next.stationName = typeof patch.stationName === 'string' && patch.stationName.trim().length > 0 ? patch.stationName.trim() : null
        }
        if (patch.host !== undefined) {
          next.host = typeof patch.host === 'string' && patch.host.trim().length > 0 ? patch.host.trim() : null
        }
        if (patch.token !== undefined) {
          next.token = typeof patch.token === 'string' && patch.token.trim().length > 0 ? patch.token.trim() : null
        }
        if (patch.publishedStation !== undefined) {
          next.publishedStation = mergePublished(patch.publishedStation)
        }
        return { bridge: next }
      })
      persist()
    },
    pushHistory: (entry) => {
      const at = entry.at ?? new Date().toISOString()
      set((s) => {
        const next: PrintHistoryEntry = {
          at, kind: entry.kind, result: entry.result,
          ...(entry.error ? { error: entry.error } : {}),
        }
        return { history: [next, ...s.history].slice(0, HISTORY_MAX) }
      })
      persist()
    },
    clearHistory: () => {
      set({ history: [] })
      persist()
    },
    resetToDefaults: () => {
      const def = cloneDefault()
      set(def)
      persist()
    },
  }
})

/** Snapshot (sin selector) — útil para callers no-React (stores como
 *  order-store.sendToKitchen que necesitan leer config sin re-render). */
export function getPrintingConfig(): PrintingConfig {
  const s = usePrintingConfigStore.getState()
  return {
    defaultPrinter: s.defaultPrinter,
    autoPrint: s.autoPrint,
    drawerOnPayment: s.drawerOnPayment,
    bridge: s.bridge,
    history: s.history,
  }
}

export function pushPrintHistory(kind: string, result: 'ok' | 'error', error?: string): void {
  usePrintingConfigStore.getState().pushHistory({ kind, result, ...(error ? { error } : {}) })
}
